import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useGetCurrentAccountReportQuery } from "../../../../api/endpoints/currentAccountReport";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { usePreAccountingCustomerOptions } from "../../../../hooks/usePreAccountingCustomerOptions";
import { InputText } from "primereact/inputtext";

const useCurrentAccountReportData = () => {
  const { th, getCurrentLanguageId } = useLanguage();
  const langId = getCurrentLanguageId();

  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [dropdownFilterValue, setDropdownFilterValue] = useState("");
  const dropdownDebounceRef = useRef(null);

  const { customerOptions, isFetching: fetchingCustomers } =
    usePreAccountingCustomerOptions({
      SearchText: customerSearchText,
      RowCount: 50,
    });

  const currentOptions = useMemo(() => {
    const opts = customerOptions || [];
    if (
      selectedCustomerId != null &&
      selectedCustomerOption &&
      !opts.some(
        (opt) => Number(opt.value) === Number(selectedCustomerOption.value)
      )
    ) {
      return [selectedCustomerOption, ...opts];
    }
    return opts;
  }, [customerOptions, selectedCustomerId, selectedCustomerOption]);

  const handleDropdownFilterInputChange = useCallback((val) => {
    setDropdownFilterValue(val);
    if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);
    dropdownDebounceRef.current = setTimeout(() => {
      setCustomerSearchText((val ?? "").trim());
    }, 400);
  }, []);

  useEffect(() => {
    return () => {
      if (dropdownDebounceRef.current)
        clearTimeout(dropdownDebounceRef.current);
    };
  }, []);

  const dropdownFilterTemplate = useCallback(
    () => (
      <div
        className="p-dropdown-filter-container"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <InputText
          value={dropdownFilterValue}
          onChange={(e) => handleDropdownFilterInputChange(e.target.value)}
          className="p-dropdown-filter w-full"
          placeholder={th("common.search") || "Ara"}
          autoFocus
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
        <span className="p-dropdown-filter-icon pi pi-search" />
      </div>
    ),
    [dropdownFilterValue, handleDropdownFilterInputChange, th]
  );

  const [pagination, setPagination] = useState({
    PageNumber: 1,
    PageSize: 10,
  });

  const handleDropdownChange = useCallback(
    (value) => {
      setDropdownFilterValue("");
      setCustomerSearchText("");

      if (value) {
        const found = customerOptions.find(
          (opt) => Number(opt.value) === Number(value)
        );
        setSelectedCustomerOption(
          found || { label: `${value}`, value: Number(value) }
        );
        setSelectedCustomerId(value);
        setPagination((p) => ({ ...p, PageNumber: 1 }));
      } else {
        setSelectedCustomerId(null);
        setSelectedCustomerOption(null);
        setPagination((p) => ({ ...p, PageNumber: 1 }));
      }
    },
    [customerOptions]
  );

  useEffect(() => {
    if (selectedCustomerId == null) return;
    const found = customerOptions.find(
      (opt) => Number(opt.value) === Number(selectedCustomerId)
    );
    if (found) setSelectedCustomerOption(found);
  }, [customerOptions, selectedCustomerId]);

  const customerDropdown = useMemo(
    () => ({
      key: "customer",
      value: selectedCustomerId,
      options: currentOptions,
      placeholder: th("preAccounting.selectCustomer") || "Müşteri Seçiniz",
      loading: fetchingCustomers,
      disabled: false,
      filterTemplate: dropdownFilterTemplate,
      onShow: () => {},
      onChange: handleDropdownChange,
      emptyMessage: th("common.noRecords") || "Kayıt bulunamadı",
    }),
    [
      selectedCustomerId,
      currentOptions,
      th,
      fetchingCustomers,
      dropdownFilterTemplate,
      handleDropdownChange,
    ]
  );

  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
  });

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const queryParams = useMemo(
    () => ({
      PageNumber: pagination.PageNumber,
      PageSize: pagination.PageSize,
      StartDate: formatDate(appliedFilters.startDate),
      EndDate: formatDate(appliedFilters.endDate),
      CustomerId: selectedCustomerId ?? undefined,
    }),
    [pagination, appliedFilters, selectedCustomerId]
  );

  const {
    currentData: listData,
    isLoading,
    isFetching,
  } = useGetCurrentAccountReportQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const rows = useMemo(() => {
    if (isFetching) return [];
    if (!listData) return [];

    const paged = listData?.items
      ? listData
      : listData?.data?.items
        ? listData.data
        : null;

    if (!paged) return [];
    return Array.isArray(paged.items) ? paged.items : [];
  }, [listData, isFetching]);

  const totalCount = useMemo(() => {
    if (isFetching) return 0;
    if (!listData) return 0;

    const paged = listData?.items
      ? listData
      : listData?.data?.items
        ? listData.data
        : null;

    if (!paged) return 0;
    return paged.totalCount || 0;
  }, [listData, isFetching]);

  const loading = isLoading || isFetching;

  const handleApplyFilters = useCallback((updated) => {
    setAppliedFilters({
      startDate: updated.startDate || null,
      endDate: updated.endDate || null,
    });
    setPagination((p) => ({ ...p, PageNumber: 1 }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setAppliedFilters({ startDate: null, endDate: null });
    setSelectedCustomerId(null);
    setSelectedCustomerOption(null);
    setDropdownFilterValue("");
    setCustomerSearchText("");
    setPagination((p) => ({ ...p, PageNumber: 1 }));
  }, []);

  const onPageChange = useCallback((event) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPagination((prev) => ({
      ...prev,
      PageNumber: newPageNumber,
      PageSize: event.rows,
    }));
  }, []);

  const filterFields = useMemo(
    () => [
      {
        field: "startDate",
        headerName:
          th("currentAccountReport.startDate") || "Başlangıç Tarihi",
        type: "date",
      },
      {
        field: "endDate",
        headerName: th("currentAccountReport.endDate") || "Bitiş Tarihi",
        type: "date",
      },
    ],
    [th]
  );

  const exportParams = useMemo(
    () => ({
      startDate: formatDate(appliedFilters.startDate),
      endDate: formatDate(appliedFilters.endDate),
      CustomerId: selectedCustomerId ?? undefined,
      LanguageId: langId,
    }),
    [appliedFilters, selectedCustomerId, langId]
  );

  return {
    rows,
    loading,
    totalCount,
    pagination,
    filterFields,
    handleApplyFilters,
    handleResetFilters,
    onPageChange,
    exportParams,
    customerDropdown,
  };
};

export default useCurrentAccountReportData;